import { useRef, useState } from "react";
import { toast } from "react-hot-toast";

import { useS3Mutation } from "@web-app/api";
import { Button } from "@web-app/components";

import { ChatMessageAttachment } from "../../api/services/chat";

import VoiceRecorder from "./VoiceRecorder";
import AttachmentIcon from "./icons/AttachmentIcon";
import { MicIcon } from "./icons/MicIcon";
import SendMessageIcon from "./icons/SendMessageIcon";

interface ChatInputProps {
  isAIConversation?: boolean;
  isLoading?: boolean;
  onSendMessage: (
    content: string,
    attachments: ChatMessageAttachment[],
  ) => void;
}

interface PendingAttachment {
  file: File;
  preview: string;
}

export default function ChatInput({
  isAIConversation,
  onSendMessage,
  isLoading,
}: ChatInputProps) {
  const [message, setMessage] = useState("");
  const [pendingAttachments, setPendingAttachments] = useState<
    PendingAttachment[]
  >([]);
  const [isUploading, setIsUploading] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const messageInputRef = useRef<HTMLTextAreaElement>(null);
  const { upload } = useS3Mutation();

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;

    const imageFiles = Array.from(files).filter((file) =>
      file.type.startsWith("image/"),
    );

    const newPendingAttachments = imageFiles.map((file) => ({
      file,
      preview: URL.createObjectURL(file),
    }));

    setPendingAttachments((prev) => [...prev, ...newPendingAttachments]);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
    messageInputRef.current?.focus();
  };

  const removePendingAttachment = (index: number) => {
    setPendingAttachments((prev) => {
      URL.revokeObjectURL(prev[index].preview);
      return prev.filter((_, i) => i !== index);
    });
  };

  const handleSendVoice = async (
    file: File,
    durationMs: number,
    waveform: number[],
  ) => {
    setIsRecording(false);
    setIsUploading(true);
    try {
      const url = await upload.mutateAsync(file);
      const attachment: ChatMessageAttachment = {
        audioDurationMs: durationMs,
        audioWaveform: waveform,
        fileName: file.name,
        fileSize: file.size,
        fileType: file.type || "audio/webm",
        url,
      };
      onSendMessage(" ", [attachment]);
    } catch (error) {
      console.error("Failed to upload voice message:", error);
      toast.error("Failed to send voice message");
    } finally {
      setIsUploading(false);
    }
  };

  const handleSend = async () => {
    if (isLoading || (!message.trim() && pendingAttachments.length === 0))
      return;

    setIsUploading(true);

    try {
      const uploadedAttachments: ChatMessageAttachment[] = [];

      for (const pending of pendingAttachments) {
        const url = await upload.mutateAsync(pending.file);
        uploadedAttachments.push({
          fileName: pending.file.name,
          fileSize: pending.file.size,
          fileType: pending.file.type,
          url: url,
        });
        URL.revokeObjectURL(pending.preview);
      }

      onSendMessage(message.trim(), uploadedAttachments);
      setMessage("");
      setPendingAttachments([]);
    } catch (error) {
      console.error("Failed to upload attachments:", error);
      toast.error("Failed to upload attachment");
    } finally {
      setIsUploading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      e.stopPropagation();
      void handleSend();
    }
  };

  const handleInput = () => {
    const textarea = messageInputRef.current;
    if (textarea) {
      textarea.style.height = "auto";
      const lineHeight = 20;
      const maxHeight = lineHeight * 4;
      textarea.style.height = `${Math.min(textarea.scrollHeight, maxHeight)}px`;
    }
  };

  return (
    <div className="border-t border-gray-200 bg-white">
      {pendingAttachments.length > 0 && (
        <div className="px-3 lg:px-4 py-1.5 lg:py-2 flex gap-1.5 lg:gap-2 overflow-x-auto border-b border-gray-200">
          {pendingAttachments.map((attachment, index) => (
            <div key={index} className="relative flex-shrink-0">
              <img
                src={attachment.preview}
                alt={attachment.file.name}
                className="h-12 w-12 lg:h-16 lg:w-16 object-cover rounded"
              />
              <Button
                onClick={() => removePendingAttachment(index)}
                className="absolute -top-1 -right-1 !rounded-full !w-4 !h-4 lg:!w-5 lg:!h-5 !p-0 !min-w-0"
                color="red"
                size="xs"
                disabled={isUploading}
                icon="xMark"
              ></Button>
            </div>
          ))}
        </div>
      )}
      {isUploading && (
        <div className="px-3 lg:px-4 py-1.5 lg:py-2 border-b border-gray-200 bg-blue-50">
          <div className="flex items-center gap-1.5 lg:gap-2 text-[10px] lg:text-xs text-blue-600">
            <div className="animate-spin rounded-full h-3.5 w-3.5 lg:h-4 lg:w-4 border-2 border-blue-600 border-t-transparent"></div>
            <span>Uploading attachments...</span>
          </div>
        </div>
      )}
      {isRecording ? (
        <VoiceRecorder
          disabled={isUploading}
          onCancel={() => setIsRecording(false)}
          onSend={handleSendVoice}
        />
      ) : (
        <div className="flex items-center gap-2 lg:gap-3 min-h-12 lg:min-h-14 px-3 lg:px-4 py-1.5 lg:py-2">
          {!isAIConversation && (
            <>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                multiple
                onChange={handleFileSelect}
                className="hidden"
              />
              <Button
                onClick={() => fileInputRef.current?.click()}
                className="flex-shrink-0 !p-0"
                plain
                disabled={isLoading || isUploading}
                aria-label="Add attachment"
              >
                <AttachmentIcon className="w-5 h-5 lg:w-6 lg:h-6" />
              </Button>
            </>
          )}
          <textarea
            ref={messageInputRef}
            rows={1}
            value={message}
            onChange={(e) => {
              setMessage(e.target.value);
              handleInput();
            }}
            onKeyDown={handleKeyDown}
            placeholder={isUploading ? "Uploading..." : "Message"}
            disabled={isUploading}
            className="flex-1 pl-2 pr-3 lg:pr-4 bg-transparent focus:outline-none placeholder:text-gray-400 text-base resize-none overflow-y-auto leading-5 touch-manipulation"
            style={{ maxHeight: "80px" }}
          />
          {!isAIConversation &&
            !message.trim() &&
            pendingAttachments.length === 0 &&
            !isUploading && (
              <Button
                onClick={() => setIsRecording(true)}
                disabled={isLoading}
                className="flex-shrink-0 !p-0"
                plain
                aria-label="Record voice message"
              >
                <MicIcon className="w-5 h-5 lg:w-6 lg:h-6" />
              </Button>
            )}
          {(message.trim() ||
            pendingAttachments.length > 0 ||
            isUploading ||
            isAIConversation) && (
            <Button
              onClick={handleSend}
              disabled={
                isLoading ||
                isUploading ||
                (!message.trim() && pendingAttachments.length === 0)
              }
              className="flex-shrink-0 !p-0"
              aria-label={isUploading ? "Uploading message" : "Send message"}
              plain
              isLoading={isUploading}
            >
              {!isUploading && (
                <SendMessageIcon className="w-5 h-5 lg:w-6 lg:h-6" />
              )}
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
